import { formatISO, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetPropertyCrops } from '@/modules/settings/property/apiHooks';
import { IPropertySettings } from '@/modules/settings/property/types';
import { useSettings } from '@/modules/settings/shared/components/logic';
import {
  LocalizedDatePicker,
  NextImage,
  SelectField,
} from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { IGeneral } from '@/shared/types';

const EditFieldCrop: React.FC<{ hasOperations: boolean }> = ({
  hasOperations,
}) => {
  const { t } = useTranslation();

  const { control, watch, formState, setValue } =
    useFormContext<IPropertySettings.ICreateEditFieldFormValues>();

  const { getCropVarieties } = useSettings();

  const { query } = useRouter();

  const { data: crops, fetchNextPage } = useGetPropertyCrops({
    propertyId: query?.id ? Number(query.id) : undefined,
    pageNumber: 1,
    pageSize: 10,
  });

  // reset variety when crop changes
  const cropId = watch('cropId');
  useDidUpdateEffect(() => {
    setValue('varietyId', null);
  }, [cropId]);

  return (
    <div className='grid grid-cols-2 gap-24'>
      <InfiniteSelectField<IGeneral.ICropSchema, false>
        id='cropId'
        isDisabled={hasOperations}
        getLabel={(obj: IGeneral.ICropSchema) => obj.name}
        getValue={(obj: IGeneral.ICropSchema) => obj.id}
        className='!rounded-xl bg-background-transparent'
        borderColor={themeColors().outline.variant}
        label={t('crop')}
        placeholder={t('crop')}
        list={crops?.pages.flatMap((page) => page.items)}
        py='10px'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full'
        formatOptionLabel={(option: IGeneral.ICropSchema) => (
          <div className='flex items-center gap-12'>
            {option.image && (
              <NextImage
                src={option.image}
                width={24}
                height={16}
                alt='country flag'
              />
            )}
            {option.name}
          </div>
        )}
        fetchNextPage={fetchNextPage}
      />
      <SelectField
        id='varietyId'
        isDisabled={hasOperations}
        getLabel={(obj: IGeneral.ICropSchema) => obj.name}
        getValue={(obj: IGeneral.ICropSchema) => obj.id}
        className='!rounded-xl bg-background-transparent'
        borderColor={themeColors().outline.variant}
        label={t('Variety')}
        placeholder={t('Variety')}
        isMulti={false}
        // isDisabled={isLoading}
        list={getCropVarieties(
          crops?.pages.flatMap((page) => page.items),
          watch('cropId')
        )}
        py='10px'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full'
      />
      <Controller
        name='plantationDate'
        control={control}
        render={({ field: { onChange, ...field } }) => (
          <LocalizedDatePicker
            inputProps={{
              label: t('Plantation Date'),
              error: !!formState.errors?.plantationDate,
              errorMessage: formState.errors?.plantationDate?.message,
              className: '!rounded-xl bg-background-transparent',
              borderColor: 'border-outline-variant',
              width: 'w-full',
              py: '!py-16',
              // disabled: isLoading,
            }}
            datePickerProps={{
              placeholderText: t('Plantation Date'),
              dateFormat: 'MMM d, yyyy',
              selected: field?.value ? parseISO(field.value) : undefined,
              disabled: hasOperations,
              onChange: (date) => {
                if (date)
                  onChange(
                    formatISO(new Date(date), { representation: 'date' })
                  );
              },
            }}
          />
        )}
      />
    </div>
  );
};

export default EditFieldCrop;
